/**
 * LocalStorage Repository for persistent cache (Browser Bundle Version)
 * Implements ISyncRepository for synchronous access to localStorage
 */

import type {
  ISyncRepository,
  IStatsProvider,
} from '../../interfaces/repository.interface.js';
import type { MemoryRepositoryConfig } from '../../config/repository.config.js';
import type { TranslationData } from '../../types/translation-data.types.js';

const STORAGE_PREFIX = 'lionrapid:';
const VERSION_KEY = `${STORAGE_PREFIX}version`;
const CURRENT_VERSION = '1';

/**
 * Stored item structure with TTL support
 */
interface StoredItem {
  value: string;
  expiry: number | null;
  timestamp: number;
}

/**
 * Configuration options for LocalStorageRepository
 */
export interface LocalStorageRepositoryConfig extends MemoryRepositoryConfig {
  options?: MemoryRepositoryConfig['options'] & {
    /** Storage key prefix (default: 'lionrapid:') */
    prefix?: string;
    /** TTL in milliseconds (default: 24 hours) */
    ttl?: number;
    /** Maximum number of entries before eviction (default: 500) */
    maxSize?: number;
  };
}

/**
 * LocalStorage-based repository for persistent translation caching
 *
 * Features:
 * - Synchronous access (localStorage is sync)
 * - TTL-based expiration
 * - Graceful fallback when localStorage unavailable (SSR, private mode)
 * - Automatic eviction when quota exceeded
 * - Version migration support
 */
export class LocalStorageRepository
  implements ISyncRepository<LocalStorageRepositoryConfig>, IStatsProvider
{
  readonly name = 'LocalStorageRepository';
  readonly type = 'memory' as const;
  readonly config: LocalStorageRepositoryConfig;

  private available: boolean;
  private prefix: string;
  private defaultTTL: number;
  private maxSize: number;

  constructor(config: LocalStorageRepositoryConfig = {}) {
    this.config = {
      enabled: config.enabled ?? true,
      priority: config.priority ?? 0, // Use build sequence by default
      options: {
        prefix: config.options?.prefix ?? STORAGE_PREFIX,
        ttl: config.options?.ttl ?? 24 * 60 * 60 * 1000, // 24 hours default
        maxSize: config.options?.maxSize ?? 500,
        ...config.options,
      },
    };

    this.prefix = this.config.options?.prefix ?? STORAGE_PREFIX;
    this.defaultTTL = this.config.options?.ttl ?? 24 * 60 * 60 * 1000;
    this.maxSize = this.config.options?.maxSize ?? 500;
    this.available = this.checkAvailability();

    if (this.available) {
      this.migrateIfNeeded();
    }
  }

  /**
   * Check if localStorage is available
   * Returns false in SSR, private browsing, or when quota is 0
   */
  private checkAvailability(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      const testKey = `${this.prefix}__test__`;
      window.localStorage.setItem(testKey, 'test');
      window.localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Handle version migrations - clear cache when version changes
   */
  private migrateIfNeeded(): void {
    try {
      const storedVersion = window.localStorage.getItem(VERSION_KEY);
      if (storedVersion !== CURRENT_VERSION) {
        this.clear();
        window.localStorage.setItem(VERSION_KEY, CURRENT_VERSION);
      }
    } catch {
      // Ignore migration errors
    }
  }

  /**
   * Get a translation value by key
   * Returns null if not found or expired
   */
  get(key: string): string | null {
    if (!this.available) return null;

    try {
      const fullKey = this.prefix + key;
      const itemStr = window.localStorage.getItem(fullKey);
      if (!itemStr) return null;

      const item: StoredItem = JSON.parse(itemStr);

      // Check TTL expiry
      if (item.expiry !== null && Date.now() > item.expiry) {
        window.localStorage.removeItem(fullKey);
        return null;
      }

      return item.value;
    } catch {
      return null;
    }
  }

  /**
   * Set a translation value
   * Automatically handles quota exceeded by evicting oldest entries
   */
  set(key: string, value: string): void {
    if (!this.available) return;

    const fullKey = this.prefix + key;
    const item: StoredItem = {
      value,
      expiry: this.defaultTTL ? Date.now() + this.defaultTTL : null,
      timestamp: Date.now(),
    };

    try {
      // Check entry count and evict if needed
      const entryCount = this.getEntryCount();
      if (entryCount >= this.maxSize) {
        this.evictOldest();
      }

      window.localStorage.setItem(fullKey, JSON.stringify(item));
    } catch (error) {
      if (this.isQuotaError(error)) {
        this.evictOldest();
        // Retry once after eviction
        try {
          window.localStorage.setItem(fullKey, JSON.stringify(item));
        } catch {
          // Give up silently
        }
      }
    }
  }

  /**
   * Remove a translation by key
   */
  remove(key: string): void {
    if (!this.available) return;

    try {
      window.localStorage.removeItem(this.prefix + key);
    } catch {
      // Ignore removal errors
    }
  }

  /**
   * Clear all LionRapid entries from localStorage
   * Preserves entries from other applications
   */
  clear(): void {
    if (!this.available) return;

    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key?.startsWith(this.prefix) && key !== VERSION_KEY) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(k => window.localStorage.removeItem(k));
    } catch {
      // Ignore clear errors
    }
  }

  /**
   * Clear all entries for a specific namespace
   */
  clearNamespace(locale: string, namespace: string): void {
    if (!this.available) return;

    const namespaceCacheKey = `@${locale}:${namespace}`;
    const keyPrefix = `${locale}:${namespace}:`;

    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (!key?.startsWith(this.prefix)) continue;

        const innerKey = key.slice(this.prefix.length);
        if (innerKey === namespaceCacheKey || innerKey.startsWith(keyPrefix)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(k => window.localStorage.removeItem(k));
    } catch {
      // Ignore errors
    }
  }

  /**
   * Get namespace translations
   */
  getNamespace(locale: string, namespace: string): TranslationData | null {
    const namespaceCacheKey = `@${locale}:${namespace}`;
    const value = this.get(namespaceCacheKey);
    if (!value) return null;

    try {
      return JSON.parse(value);
    } catch {
      this.remove(namespaceCacheKey);
      return null;
    }
  }

  /**
   * Set namespace translations
   */
  setNamespace(
    locale: string,
    namespace: string,
    translations: TranslationData
  ): void {
    const namespaceCacheKey = `@${locale}:${namespace}`;
    this.set(namespaceCacheKey, JSON.stringify(translations));
  }

  /**
   * Get all cache entries for debugging
   */
  getEntries(): Record<string, string> {
    const entries: Record<string, string> = {};
    if (!this.available) return entries;

    try {
      for (let i = 0; i < window.localStorage.length; i++) {
        const fullKey = window.localStorage.key(i);
        if (!fullKey?.startsWith(this.prefix) || fullKey === VERSION_KEY) {
          continue;
        }

        const key = fullKey.slice(this.prefix.length);
        const value = this.get(key);
        if (value !== null) {
          entries[key] = value;
        }
      }
    } catch {
      // Ignore errors
    }

    return entries;
  }

  /**
   * Get repository configuration
   */
  getConfig(): LocalStorageRepositoryConfig {
    return this.config;
  }

  /**
   * Check if localStorage is available
   */
  isAvailable(): boolean {
    return this.available;
  }

  /**
   * Get the number of entries in the cache
   */
  private getEntryCount(): number {
    if (!this.available) return 0;

    let count = 0;
    try {
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key?.startsWith(this.prefix) && key !== VERSION_KEY) {
          count++;
        }
      }
    } catch {
      // Ignore errors
    }

    return count;
  }

  /**
   * Check if error is a quota exceeded error
   */
  private isQuotaError(error: unknown): boolean {
    if (error instanceof DOMException) {
      // Most browsers
      return (
        error.name === 'QuotaExceededError' ||
        error.name === 'NS_ERROR_DOM_QUOTA_REACHED'
      );
    }
    return false;
  }

  /**
   * Evict oldest 25% of entries when quota is exceeded
   */
  private evictOldest(): void {
    if (!this.available) return;

    try {
      // Collect all entries with timestamps
      const entries: Array<{ key: string; timestamp: number }> = [];

      for (let i = 0; i < window.localStorage.length; i++) {
        const fullKey = window.localStorage.key(i);
        if (!fullKey?.startsWith(this.prefix) || fullKey === VERSION_KEY) {
          continue;
        }

        try {
          const itemStr = window.localStorage.getItem(fullKey);
          if (itemStr) {
            const item: StoredItem = JSON.parse(itemStr);
            entries.push({ key: fullKey, timestamp: item.timestamp });
          }
        } catch {
          // Remove corrupted entry
          window.localStorage.removeItem(fullKey);
        }
      }

      // Sort by timestamp (oldest first)
      entries.sort((a, b) => a.timestamp - b.timestamp);

      // Remove oldest 25%
      const removeCount = Math.max(1, Math.floor(entries.length * 0.25));
      for (let i = 0; i < removeCount && i < entries.length; i++) {
        window.localStorage.removeItem(entries[i].key);
      }
    } catch {
      // Ignore eviction errors
    }
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; maxSize: number; usage: string } {
    const size = this.getEntryCount();
    const usage =
      this.maxSize > 0 ? `${Math.round((size / this.maxSize) * 100)}%` : '0%';

    return {
      size,
      maxSize: this.maxSize,
      usage,
    };
  }
}
